import tw from 'tailwind-styled-components'
import {DivWithHint} from '@components/common/hint/hint'
import {cn} from '@containers/html-utils'
import {Color, Size, State} from './types'

const sizeMixin = (size: Size, multiple?: boolean): string => {
  if (multiple) {
    return 'p-2'
  }

  switch (size) {
    case 'sm':
      return cn('px-12', 'py-8', 'h-32')
    case 'md':
      return cn('px-12', 'py-12', 'h-40')
    case 'lg':
      return cn('px-12', 'py-16', 'h-48')
  }
}

const colorMixin = (
  color: Color,
  disabled?: boolean,
  forcedState?: State,
  multiple?: boolean
): string => {
  switch (color) {
    case 'primary':
      return primaryMixin(disabled, forcedState, multiple)
    case 'secondary':
      return secondaryMixin(disabled, forcedState, multiple)
    case 'notification-primary':
      return notificationPrimaryMixin
    case 'error':
      return errorMixin(disabled, forcedState, multiple)
  }
}

const primaryMixin = (
  disabled?: boolean,
  forcedState?: State,
  multiple?: boolean
): string => {
  if (disabled) {
    return cn('bg-background-disabled', 'text-disabled-new')
  }

  const cx: string[] = ['text-icon-invert']
  if (forcedState === 'active') {
    cx.push('bg-action-pressed')
  } else if (forcedState === 'hover') {
    cx.push('bg-action-hover')
  } else if (forcedState === 'default') {
    cx.push('bg-action-default')
  } else {
    cx.push('bg-action-default')
    if (!multiple) {
      cx.push('hover:bg-action-hover', 'active:bg-action-pressed')
    }
  }
  return cx.join(' ')
}

const secondaryMixin = (
  disabled?: boolean,
  forcedState?: State,
  multiple?: boolean
): string => {
  if (disabled) {
    return cn(
      'bg-background-primary',
      'text-disabled-new',
      'shadow-border-1',
      'shadow-line-disabled'
    )
  }

  const cx: string[] = ['text-icon-secondary']
  if (forcedState === 'active') {
    cx.push('bg-background-active')
  } else if (forcedState === 'hover') {
    cx.push('bg-background-hover')
  } else if (forcedState === 'default') {
    cx.push('bg-background-primary', 'shadow-border-1', 'shadow-line-base')
  } else {
    cx.push('bg-background-primary', 'shadow-border-1', 'shadow-line-base')
    if (!multiple) {
      cx.push(
        'hover:bg-background-hover',
        'hover:shadow-none',
        'active:bg-background-active',
        'active:shadow-none'
      )
    }
  }
  return cx.join(' ')
}

const notificationPrimaryMixin = `
  text-icon-invert
  bg-notification-primary
`

const errorMixin = (
  disabled?: boolean,
  _forcedState?: State,
  _multiple?: boolean
): string => {
  if (disabled) {
    return 'pointer-events-none bg-action-primary-disabled text-disabled-new'
  }

  return 'bg-action-attention-default text-invert hover:bg-action-attention-hover active:bg-action-attention-pressed'
}

export const TwButtonNew = tw(DivWithHint)<{
  $color: Color
  $size: Size
  $fitWidth?: boolean
  $disabled?: boolean
  $forcedState?: State
  $multiple?: boolean
}>`
  relative
  select-none
  rounded-8
  text-sm-semibold
  flex
  items-center
  justify-center
  ${p =>
    cn(
      !p.$fitWidth && 'w-fit',
      !p.$multiple && 'gap-4',
      !p.$disabled && 'cursor-pointer',
      sizeMixin(p.$size, p.$multiple),
      colorMixin(p.$color, p.$disabled, p.$forcedState, p.$multiple)
    )}
`

export const TwText = tw.span`
  whitespace-nowrap
  overflow-hidden
  text-ellipsis
`

export const TwSeparator = tw.div`
  w-1
  h-16
  mx-2
  bg-invert
`
